/**
 * Step 5: Success Screen
 * 
 * Shows the created address with QR code and next steps.
 * 
 * @module addresses/wizard
 */

import React, { useState } from 'react';
import { Heading } from "@/components/ui/typography";
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Button } from '@/components/ui/button';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { NetworkIcon } from '@/components/NetworkIcons';
import { toast } from '@/hooks/use-toast';
import { QR } from '@/components/ui/QR';
import type { WizardFormData } from './types';
import { NETWORK_LABELS } from './types';

interface SuccessScreenProps {
  address: string;
  formData: WizardFormData;
  onViewAllAddresses: () => void;
  onCreateAnother: () => void;
}

export const SuccessScreen: React.FC<SuccessScreenProps> = ({
  address,
  formData,
  onViewAllAddresses,
  onCreateAnother,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy address');
    }
  };

  const handleDownloadQR = async () => {
    try {
      const qrcode = await import('qrcode');
      const dataUrl = await qrcode.toDataURL(address, {
        width: 300,
        margin: 2,
        errorCorrectionLevel: 'M'
      });
      
      const downloadLink = document.createElement('a');
      downloadLink.download = `supabank-address-${address.slice(0, 8)}.png`;
      downloadLink.href = dataUrl;
      downloadLink.click();
      
      toast.success('QR code downloaded');
    } catch (err) {
      console.error('Failed to download QR code:', err);
      toast.error('Failed to download QR code');
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Supabank Payment Address',
      text: `Send ${formData.asset} on ${NETWORK_LABELS[formData.network]} to this address:`,
      url: address,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        handleCopyAddress();
      }
    } catch (err) {
      console.log('Share cancelled or failed');
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <div className={cn("w-16 h-16 mx-auto rounded-full flex items-center justify-center", semanticTokens.positive.bg)}>
          <SFIcon icon={sfIcons.check} size="xl" className={semanticTokens.positive.fg} />
        </div>
        <div className="space-y-2">
          <Heading level={2} className={cn("sm:text-display-sm", semanticTokens.fg.default)}>
            Your Address is Ready!
          </Heading>
          <p className={cn("text-sm sm:text-base", semanticTokens.fg.muted)}>
            Share this address to start receiving crypto payments
          </p>
        </div>
      </div>

      <div className={cn(
        "p-6 rounded-xl border text-center",
        semanticTokens.bg.surface,
        semanticTokens.border.default
      )}>
        <div className={cn("p-4 rounded-lg inline-block mb-4", semanticTokens.bg.surface)}>
          <QR
            value={address}
            size={180}
            alt={`QR code for ${address.slice(0, 8)}...`}
            className="mx-auto"
          />
        </div>

        <div className="space-y-3">
          <div className={cn(
            "p-3 rounded-lg font-mono text-sm break-all",
            semanticTokens.bg.muted
          )}>
            {address}
          </div>
          
          <Button
            onClick={handleCopyAddress}
            variant={copied ? "default" : "outline"}
            className="w-full gap-2"
          >
            {copied ? (
              <>
                <SFIcon icon={sfIcons.check} size="sm" />
                Copied!
              </>
            ) : (
              <>
                <SFIcon icon={sfIcons.copy} size="sm" />
                Copy Address
              </>
            )}
          </Button>
        </div>

        <div className={cn("flex items-center justify-center gap-3 mt-4 pt-4 border-t", semanticTokens.border.subtle)}>
          <div className="flex items-center gap-2">
            <CurrencyIcon symbol={formData.asset} size="sm" />
            <span className={cn("text-sm font-medium", semanticTokens.fg.default)}>
              {formData.asset}
            </span>
          </div>
          <span className={semanticTokens.fg.subtle}>on</span>
          <div className="flex items-center gap-2">
            <NetworkIcon network={formData.network} className="w-4 h-4" />
            <span className={cn("text-sm font-medium", semanticTokens.fg.default)}>
              {NETWORK_LABELS[formData.network]}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Button variant="outline" onClick={handleShare} className="flex-col gap-2 h-auto py-4">
          <SFIcon icon={sfIcons.send} size="md" />
          <span className="text-xs">Share</span>
        </Button>
        <Button variant="outline" onClick={handleDownloadQR} className="flex-col gap-2 h-auto py-4">
          <SFIcon icon={sfIcons.download} size="md" />
          <span className="text-xs">Download QR</span>
        </Button>
        <Button variant="outline" onClick={handleCopyAddress} className="flex-col gap-2 h-auto py-4">
          <SFIcon icon={sfIcons.copy} size="md" />
          <span className="text-xs">Copy</span>
        </Button>
        <Button variant="outline" className="flex-col gap-2 h-auto py-4" asChild>
          <a href={`mailto:?subject=Payment%20Address&body=Send%20${formData.asset}%20to:%20${address}`}>
            <SFIcon icon={sfIcons.mail} size="md" />
            <span className="text-xs">Email</span>
          </a>
        </Button>
      </div>

      <div className={cn(
        "p-4 rounded-lg border",
        semanticTokens.info.bg,
        semanticTokens.info.border
      )}>
        <h3 className={cn("font-medium mb-3 flex items-center gap-2", semanticTokens.info.fg)}>
          <SFIcon icon={sfIcons.info} size="sm" />
          What happens next
        </h3>
        <ul className="space-y-2">
          {formData.destinationType === 'fiat' ? (
            <>
              <li className={cn("flex items-start gap-2 text-sm", semanticTokens.fg.muted)}>
                <span className={cn("mt-0.5", semanticTokens.positive.accent)}>1.</span>
                <span>Share this address with anyone who needs to pay you</span>
              </li>
              <li className={cn("flex items-start gap-2 text-sm", semanticTokens.fg.muted)}>
                <span className={cn("mt-0.5", semanticTokens.positive.accent)}>2.</span>
                <span>When they send {formData.asset}, we detect it automatically</span>
              </li>
              <li className={cn("flex items-start gap-2 text-sm", semanticTokens.fg.muted)}>
                <span className={cn("mt-0.5", semanticTokens.positive.accent)}>3.</span>
                <span>We convert to {formData.currency} at market rates</span>
              </li>
              <li className={cn("flex items-start gap-2 text-sm", semanticTokens.fg.muted)}>
                <span className={cn("mt-0.5", semanticTokens.positive.accent)}>4.</span>
                <span>Funds arrive in your bank account (1-2 business days)</span>
              </li>
            </>
          ) : (
            <>
              <li className={cn("flex items-start gap-2 text-sm", semanticTokens.fg.muted)}>
                <span className={cn("mt-0.5", semanticTokens.positive.accent)}>1.</span>
                <span>Share this address with anyone who needs to pay you</span>
              </li>
              <li className={cn("flex items-start gap-2 text-sm", semanticTokens.fg.muted)}>
                <span className={cn("mt-0.5", semanticTokens.positive.accent)}>2.</span>
                <span>When they send crypto, we detect it automatically</span>
              </li>
              <li className={cn("flex items-start gap-2 text-sm", semanticTokens.fg.muted)}>
                <span className={cn("mt-0.5", semanticTokens.positive.accent)}>3.</span>
                <span>Funds are forwarded to your wallet instantly</span>
              </li>
            </>
          )}
        </ul>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="outline" onClick={onCreateAnother} className="flex-1 gap-2">
          <SFIcon icon={sfIcons.fingerprint} size="sm" />
          Create Another Address
        </Button>
        <Button onClick={onViewAllAddresses} className="flex-1 gap-2">
          View All Addresses
          <SFIcon icon={sfIcons.next} size="sm" />
        </Button>
      </div>
    </div>
  );
};

export default SuccessScreen;

